import { SessionStayPeriod } from '../../session/model';
import { CancelConditionType, Rate } from './rate.model';
import { Money, MONEY_SYMBOLS, MoneyPrice, Tax } from '../../../core/money';
import {
  RoomImageMetadata,
  RoomMetadata,
  RoomTemplateImage,
} from '../../room/model';
import { Language } from '../../../core';

export const VAT_INCLUDED_MESSAGE = Language.getTranslation('vatIncluded');
export const VAT_EXCLUDED_MESSAGE = Language.getTranslation('vatExcluded');

const getDatePartOfRateId = (stayPeriod: SessionStayPeriod) => {
  const arrivalDate = stayPeriod.arrivalDate.replace(/-/g, '');
  const departureDate = stayPeriod.departureDate.replace(/-/g, '');
  return `${arrivalDate};${departureDate}`;
};

const getIdPartOfRateId = (rateId: Rate['rateId']) => {
  const partsOfRateId = rateId.split(';');
  return partsOfRateId[partsOfRateId.length - 1];
};

const getOnSiteTaxesMessageFormatted = (rate: Rate) => {
  const onSiteTaxesDetail = rate.taxes.onSite.details[0];
  return onSiteTaxesDetail
    ? `${onSiteTaxesDetail.name}: ${onSiteTaxesDetail.description} (${onSiteTaxesDetail.amount.converted.text})`
    : '';
};

const multiplyMoney = (
  money: MoneyPrice,
  times: number,
  alwaysShowDecimal = false
) => {
  const { value } = money;
  const multiplied = value.amount * times;
  const isInteger = multiplied % 1 === 0;
  const showDecimal = alwaysShowDecimal || !isInteger;
  const decimal = showDecimal ? 2 : 0;
  return `${
    MONEY_SYMBOLS[value.currency as keyof typeof MONEY_SYMBOLS] ||
    value.currency
  } ${Number.parseFloat(String(multiplied)).toFixed(decimal)}`;
};

const sumMoney = (moneys: MoneyPrice[], currency: Money['currency']) => {
  const sum = moneys.reduce((acc, money) => {
    return acc + money.value.amount;
  }, 0);

  const isInteger = sum % 1 === 0;
  const decimal = isInteger ? 0 : 2;
  return `${
    MONEY_SYMBOLS[currency as keyof typeof MONEY_SYMBOLS] || currency
  } ${sum.toFixed(decimal)}`;
};

const getTaxesMessageFormatted = (taxes: Tax) => {
  if (taxes.excluded.amount.value.amount === 0) {
    return VAT_INCLUDED_MESSAGE;
  }

  return VAT_EXCLUDED_MESSAGE;
};

const getDefaultCancelConditionMessage = (rate: Rate) => {
  const cancelCondition = rate.description.cancelConditions.summary.find(
    (summary) => summary.value === CancelConditionType.Default
  );
  return cancelCondition && cancelCondition.text;
};

const getDefaultCancelConditionName = (rate: Rate) => {
  const cancelCondition = rate.description.cancelConditions.name.find(
    (name) => name.value === CancelConditionType.Default
  );
  return cancelCondition && cancelCondition.text;
};

const getCoverImage = (rate: Rate) => {
  if (rate.description.pictures.length) {
    return rate.description.pictures[0].templates.find(
      (t) => t.name === RoomTemplateImage.Original
    );
  }

  return {} as RoomImageMetadata;
};

const getSummaryType = (summary: RoomMetadata<string>[], type: string) => {
  return summary.find((s) => s.value === type);
};

const getCheapestRate = (rates: Rate[]) => {
  if (!rates) {
    return {} as Rate;
  }

  let indexOfMin = 0;
  for (let i = 1; i < rates.length; i++) {
    const amountToCompare = (index: number) =>
      rates[index].price.totalPrice.original.value.amount;
    if (amountToCompare(indexOfMin) > amountToCompare(i)) {
      indexOfMin = i;
    }
  }
  return rates[indexOfMin];
};

export const RateHelper = {
  getDatePartOfRateId,
  getIdPartOfRateId,
  multiplyMoney,
  sumMoney,
  getOnSiteTaxesMessageFormatted,
  getTaxesMessageFormatted,
  getDefaultCancelConditionMessage,
  getDefaultCancelConditionName,
  getCoverImage,
  getSummaryType,
  getCheapestRate,
};
